import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from '../lib/routerCompat';
import { useToast } from '../ToastContext';
import ProfileBuilder from '../components/ProfileBuilder';
import { db, HandleUnavailableError } from '../services/db';
import {
  ProfileFieldError,
  draftFields,
  draftFromRow,
  firstIncompleteStep,
} from '../lib/creatorProfile';
import type { ProfileBuilderStep } from '../lib/creatorProfile';
import type { CreatorProfileDraft, CreatorRow } from '../types';

/**
 * Creator profile onboarding (PIT-48). Wraps {@link ProfileBuilder} with the
 * two things the form itself shouldn't know about: persistence and where to go
 * when it's done.
 *
 * Resumption is row-based. The builder saves each stage as it is completed, so
 * a creator who leaves halfway already owns a partial `creators` row; coming
 * back reloads it, seeds the form from it, and opens on the first stage still
 * missing something. Nothing here can produce a second row — the first stage
 * goes through `db.createMyCreator()`, which returns the existing row rather
 * than inserting when one is already owned (and `creators.user_id` is unique),
 * and every later stage updates the row this session is holding.
 */
export default function OnboardingView(): JSX.Element {
  const navigate = useNavigate();
  const showToast = useToast();
  const [existing, setExisting] = useState<CreatorRow | null>(null);
  // Demo mode has nothing to look up, so it is resolved before the first paint.
  const [resolved, setResolved] = useState<boolean>(db.demoMode);
  // The row this session owns, tracked outside React state so the save handler
  // reads the id created moments earlier rather than a stale render's copy.
  const rowRef = useRef<CreatorRow | null>(null);

  // Demo mode has no session and no backend: the flow is walkable, and
  // `db.getMyCreator()` would hand back the bundled showcase profile, so the
  // builder deliberately starts blank instead of prefilling someone else's row.
  useEffect(() => {
    if (db.demoMode) return undefined;
    let cancelled = false;
    db.getMyCreator()
      .then((row) => {
        if (cancelled) return;
        rowRef.current = row;
        setExisting(row);
      })
      .catch(() => {
        // Treat a failed lookup as "nothing built yet": the builder still
        // renders, and the first save resolves the row idempotently.
      })
      .finally(() => {
        if (!cancelled) setResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist one completed stage. Rethrows so the builder keeps the creator on
  // the stage with their input intact when a write fails.
  const saveStep = async (
    draft: CreatorProfileDraft,
    steps: readonly ProfileBuilderStep[]
  ): Promise<void> => {
    if (db.demoMode) return;
    const fields = draftFields(draft, steps);
    try {
      // No row yet — the first stage creates it. `createMyCreator` resolves to
      // the existing row if another tab got there first, so this can't double
      // up; later stages update whatever row that produced.
      const saved = rowRef.current
        ? await db.updateMyCreator(fields)
        : await db.createMyCreator(fields);
      // Keep the row we already hold if a write resolves to nothing (an expired
      // session persists no row either way) so the next stage still updates
      // rather than trying to insert a second one.
      if (saved) rowRef.current = saved;
    } catch (err) {
      // A handle the database refused (taken between the availability check and
      // this write, or reserved) belongs under the handle input, not in a toast
      // that says nothing about which field to change.
      if (err instanceof HandleUnavailableError) {
        throw new ProfileFieldError('handle', err.message);
      }
      showToast('Could not save your profile — please try again');
      throw new Error('save failed');
    }
  };

  // Best-effort "is this free?" for the handle field. RLS hides unpublished
  // profiles, so this can only ever be a hint; the unique index decides on save.
  const checkHandle = (handle: string): Promise<boolean> => db.isHandleAvailable(handle);

  const complete = (): void => {
    showToast(db.demoMode ? 'Demo mode — profile not saved' : 'Profile created');
    navigate({ to: '/creator' });
  };

  if (!resolved) {
    return (
      <div className="view">
        <div className="panel">
          <div className="creator-name" style={{ fontSize: 27 }}>
            Loading your profile…
          </div>
        </div>
      </div>
    );
  }

  return (
    <ProfileBuilder
      initialDraft={draftFromRow(existing)}
      initialStep={firstIncompleteStep(existing)}
      onSave={saveStep}
      onComplete={complete}
      // No backend to ask in demo mode: the field simply stays quiet, and the
      // upload inputs disable themselves so the emoji placeholders stand in.
      onCheckHandle={db.demoMode ? undefined : checkHandle}
      onUploadImage={db.demoMode ? undefined : (file, kind) => db.uploadCreatorMedia(file, kind)}
      demoMode={db.demoMode}
    />
  );
}
